import {
  BrandAssetDto,
  BrandAssetKind,
  BrandAssetMaxBytes,
  brandAssetAccept,
} from "@arrowsup/platform-dtos";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, Link, Tooltip, Typography } from "@mui/material";
import { createContext, useContext, useMemo, useRef } from "react";
import { useNotify } from "react-admin";
import { useBrandAssetMutations } from "../../logic/brand-kit/use-brand-kit";

/** The assets brand kit views can show, by id. */
export const BrandAssetsContext = createContext<
  ReadonlyMap<string, BrandAssetDto>
>(new Map());

/** Adds `assets` to the ones the views inside can show. */
export const BrandAssetsProvider: React.FC<{
  assets: readonly BrandAssetDto[];
  children: React.ReactNode;
}> = ({ assets, children }) => {
  const outer = useContext(BrandAssetsContext);

  const value = useMemo(() => {
    const merged = new Map(outer);
    assets.forEach((asset) => merged.set(asset.id, asset));

    return merged;
  }, [outer, assets]);

  return (
    <BrandAssetsContext.Provider value={value}>
      {children}
    </BrandAssetsContext.Provider>
  );
};

export const useBrandAsset = (id?: string): BrandAssetDto | undefined => {
  const assets = useContext(BrandAssetsContext);

  return id === undefined ? undefined : assets.get(id);
};

const Unknown = () => (
  <Typography variant="body2" color="text.disabled">
    Unknown asset
  </Typography>
);

/** An image asset's preview, linking to the original. */
export const BrandAssetThumbnail: React.FC<{ id: string; size?: number }> = ({
  id,
  size = 96,
}) => {
  const asset = useBrandAsset(id);

  if (!asset) {
    return <Unknown />;
  }

  return (
    <Tooltip title={asset.name}>
      <Link
        href={asset.url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: "inline-block" }}
      >
        <Box
          component="img"
          src={asset.previewUrl ?? asset.url}
          alt={asset.name}
          sx={{
            display: "block",
            maxWidth: size * 2,
            maxHeight: size,
            objectFit: "contain",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "grey.100",
          }}
        />
      </Link>
    </Tooltip>
  );
};

/** A link that downloads a font asset. */
export const BrandAssetFileLink: React.FC<{ id: string }> = ({ id }) => {
  const asset = useBrandAsset(id);

  if (!asset) {
    return <Unknown />;
  }

  return (
    <Link href={asset.url} target="_blank" rel="noopener noreferrer">
      {asset.name}
    </Link>
  );
};

const kindNames: Record<BrandAssetKind, string> = {
  [BrandAssetKind.Image]: "an image",
  [BrandAssetKind.Font]: "a font",
};

/** Uploads a file to the library, optionally requiring one `kind`. */
export const BrandAssetUploadButton: React.FC<{
  kind?: BrandAssetKind;
  onUploaded?: (asset: BrandAssetDto) => void;
}> = ({ kind, onUploaded }) => {
  const notify = useNotify();
  const { upload } = useBrandAssetMutations();
  const input = useRef<HTMLInputElement>(null);

  const onFile = (file?: File) => {
    if (!file) {
      return;
    }

    if (file.size > BrandAssetMaxBytes) {
      const limit = BrandAssetMaxBytes / 1024 / 1024;
      notify(`${file.name} is larger than ${limit}MB`, { type: "error" });

      return;
    }

    upload.mutate(file, {
      onSuccess: (asset) => {
        if (kind && asset.kind !== kind) {
          notify(`Uploaded ${asset.name}, but it isn't ${kindNames[kind]}`, {
            type: "warning",
          });

          return;
        }

        notify(`Uploaded ${asset.name}`);
        onUploaded?.(asset);
      },
    });
  };

  return (
    <>
      <input
        ref={input}
        type="file"
        hidden
        accept={brandAssetAccept(kind)}
        aria-label="Upload file"
        onChange={(e) => {
          onFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <Button
        startIcon={<UploadFileIcon />}
        disabled={upload.isPending}
        onClick={() => input.current?.click()}
      >
        {upload.isPending ? "Uploading..." : "Upload"}
      </Button>
    </>
  );
};
